/* eslint-disable no-self-assign */
import courseProgram from '@/api/courseProgram'
import unionBy from 'lodash/unionBy'
import get from 'lodash/get'
import cloneDeep from 'lodash/cloneDeep'

const INITIAL_STATE = {
  courseProgramList: {},
  selectedProgram: null,
}

export default {
  namespace: 'courseProgram',

  state: INITIAL_STATE,

  reducers: {
    init() {
      return { ...INITIAL_STATE }
    },
    setCourseProgramList(state, { payload, infinite }) {
      let newList = payload
      if (infinite) {
        const { courseProgramList } = state

        if (get(courseProgramList, 'paging.pageIndex') !== get(payload, 'paging.pageIndex')) {
          newList = {
            data: unionBy(courseProgramList.data || [], payload.data, 'programId'),
            paging: payload.paging,
          }
        }
      }

      return { ...state, courseProgramList: newList }
    },
    setProgram(state, { payload }) {
      const { courseProgramList, selectedProgram } = state
      const newData = cloneDeep(courseProgramList.data)
      const foundIndex = newData.findIndex(p => p.programId === payload.programId)

      if (foundIndex >= 0) {
        newData[foundIndex] = payload
      }

      const newSelected =
        payload.programId === selectedProgram?.programId ? payload : selectedProgram

      return {
        ...state,
        courseProgramList: { ...courseProgramList, data: newData },
        selectedProgram: newSelected,
      }
    },
    setSelectedProgram(state, { payload }) {
      return { ...state, selectedProgram: payload }
    },
    setSelectedProgramOrder(state, { payload }) {
      const { selectedProgram } = state
      const newSelectedProgram = { ...selectedProgram }
      const { courseTempList = [] } = newSelectedProgram

      const result = [...courseTempList]
      const { from, to } = payload
      const [removed] = result.splice(from, 1)

      result.splice(to, 0, removed)

      newSelectedProgram.courseTempList = result

      return { ...state, selectedProgram: newSelectedProgram }
    },
    removeCourseProgram(state, { payload }) {
      const { courseProgramList } = state
      const data = courseProgramList.data.filter(p => p.programId !== payload)
      const newList = { ...courseProgramList, data }

      return { ...state, courseProgramList: newList }
    },
    updateCourseProgram(state, { payload }) {
      const { courseProgramList } = state
      const { data } = courseProgramList
      const newData = cloneDeep(data)
      const index = newData.findIndex(d => d.programId === payload.courseProgramId)
      newData[index].name = payload.name
      const newList = cloneDeep(courseProgramList)
      newList.data = newData

      return { ...state, courseProgramList: newList }
    },
  },

  effects: {
    *getCourseProgram(
      { payload, infinite, successCallback, errorCallback },
      { call, put, select },
    ) {
      const { account } = yield select(state => state.auth)
      const response = yield call(courseProgram.getCourseProgram, {
        params: { accountId: get(account, 'accountId'), ...payload },
      })
      const { status, data: resData } = response
      let hasError = true

      if (status === 200) {
        hasError = false

        yield put({ type: 'courseProgram/setCourseProgramList', payload: resData, infinite })

        successCallback?.(resData)
      }
      if (hasError) {
        errorCallback?.()
      }
    },

    *getCourseProgramById({ payload, successCallback, errorCallback }, { call, select }) {
      const { account } = yield select(state => state.auth)
      const response = yield call(courseProgram.getCourseProgramById, {
        ...payload,
        params: { accountId: get(account, 'accountId') },
      })
      const { status, data: resData } = response
      let hasError = true

      if (status === 200) {
        hasError = false

        successCallback?.(resData.data)
      }
      if (hasError) {
        errorCallback?.()
      }
    },

    *createCourseProgram({ payload, successCallback, errorCallback }, { call, select, put }) {
      const { account } = yield select(state => state.auth)
      const response = yield call(courseProgram.createCourseProgram, {
        data: {
          ...payload,
        },
        params: { accountId: get(account, 'accountId'), ...payload },
      })

      const { status, data: resData } = response

      let error = ''

      if (status === 200) {
        const { success, message } = resData
        if (success) {
          error = undefined
          successCallback?.()
        } else {
          error = message
        }
      }

      if (error) {
        yield put({ type: 'app/notify', payload: { message: error, severity: 'error' } })
        errorCallback?.(error)
      }
    },

    *editCourseProgram({ payload, successCallback, errorCallback }, { call, select, put }) {
      const { account } = yield select(state => state.auth)
      const response = yield call(courseProgram.editCourseProgram, {
        data: payload,
        params: { accountId: get(account, 'accountId') },
      })

      const { status, data: resData = {} } = response

      let error = ''

      const { success, message } = resData

      if (status === 200) {
        if (success) {
          error = undefined
          yield put({ type: 'courseProgram/updateCourseProgram', payload })
          successCallback?.()
        } else {
          error = message
        }
      } else {
        error = message
      }

      if (error) {
        yield put({ type: 'app/notify', payload: { message: error, severity: 'error' } })
        errorCallback?.(error)
      }
    },

    *editProgramCourseOrder({ payload, successCallback, errorCallback }, { call, select, put }) {
      const { account } = yield select(state => state.auth)
      const { from, to, programId, ...params } = payload

      yield put({ type: 'courseProgram/setSelectedProgramOrder', payload: { from, to } })

      const response = yield call(courseProgram.editProgramCourseOrder, {
        data: {},
        programId,
        params: { accountId: get(account, 'accountId'), ...params, programId, order: to },
      })

      const { status, data: resData = {} } = response

      let error = ''

      const { success, message } = resData

      if (status === 200) {
        if (success) {
          error = undefined

          successCallback?.()
        } else {
          error = message
        }
      } else {
        error = message
      }

      if (error) {
        errorCallback?.(error)
      }
    },

    *editCourseProgramCourse({ payload, successCallback, errorCallback }, { call, select, put }) {
      const { account } = yield select(state => state.auth)
      const { programId } = payload
      const response = yield call(courseProgram.editCourseProgramCourse, {
        programId,
        data: payload,
        params: { accountId: get(account, 'accountId') },
      })

      const { status, data: resData } = response

      let error = ''

      if (status === 200) {
        const { success, message } = resData
        if (success) {
          error = undefined
          successCallback?.()
        } else {
          error = message
        }
      }

      if (error) {
        yield put({ type: 'app/notify', payload: { message: error, severity: 'error' } })
        errorCallback?.(error)
      }
    },

    *deleteCourseProgram({ payload, successCallback, errorCallback }, { call, put, select }) {
      const { account } = yield select(state => state.auth)
      const response = yield call(courseProgram.deleteCourseProgram, {
        ...payload,
        params: { accountId: get(account, 'accountId') },
      })

      const { status, data: resData } = response

      let error = ''

      if (status === 200) {
        const { success, message } = resData
        if (success) {
          error = undefined
          yield put({ type: 'courseProgram/removeCourseProgram', payload: payload.programId })
          successCallback?.()
        } else {
          error = message
        }
      }
      if (error) {
        errorCallback?.(error)
      }
    },

    *deleteCourseProgramCourse({ payload, successCallback, errorCallback }, { call, select }) {
      const { account } = yield select(state => state.auth)
      const { programId, ...params } = payload
      const response = yield call(courseProgram.deleteCourseProgramCourse, {
        programId,
        params: { accountId: get(account, 'accountId'), ...params },
      })

      const { status, data: resData } = response

      let error = ''

      if (status === 200) {
        const { success, message } = resData
        if (success) {
          error = undefined

          successCallback?.()
        } else {
          error = message
        }
      }
      if (error) {
        errorCallback?.(error)
      }
    },
  },
}
